import { useCallback, useState } from 'react'
import { ClockCounterClockwise } from '@phosphor-icons/react'
import { Switch } from '@headlessui/react'

import { ListId, listReset } from '@/main/features/List/listSlice'

import { useAppDispatch } from '@/main/app/hooks/storeHooks'

import { Button } from '@/main/components/Button/Button'
import { Modal } from '@/main/components/Modal/Modal'

export function ListResetDialog({
  listId,
  isOpen,
  onClose,
}: {
  listId: ListId
  isOpen: boolean
  onClose: (goToStep1?: boolean) => void
}) {
  const dispatch = useAppDispatch()
  const [resetProfile, setResetProfile] = useState(false)

  const onReset = useCallback(() => {
    dispatch(listReset({ listId, resetProfile }))
    onClose(true)
  }, [dispatch, listId, onClose, resetProfile])

  return (
    <Modal isOpen={isOpen} onClose={() => onClose()} onConfirm={onReset}>
      <Modal.Title>Reset Form and Start New Request</Modal.Title>
      <Modal.Description>
        <strong className="twc-font-semibold">
          Reset the form to start a new request, keeping the current props in your cart.
        </strong>{' '}
      </Modal.Description>

      <Modal.Body>
        <p>Submit new references, dates, and pick-up/delivery info to share with ACME.</p>
        <p>
          Not done with this project? Create a New Project instead from the{' '}
          <span className="twc-bg-sky-200 dark:twc-bg-sky-800 dark:twc-text-slate-50 twc-font-medium twc-px-1.5 twc-py-1 twc-ml-0.5 twc-text-sm twc-rounded">
            Options
          </span>{' '}
          menu.
        </p>

        <Switch.Group>
          <div className="twc-flex twc-items-center twc-mt-6 twc-mb-1 twc-gap-2">
            <Switch
              checked={resetProfile}
              onChange={setResetProfile}
              className={`${
                resetProfile
                  ? '!twc-bg-emerald-600 dark:!twc-bg-emerald-600'
                  : '!twc-bg-gray-200 dark:!twc-bg-gray-600'
              } twc-relative twc-inline-flex twc-h-6 twc-w-11 twc-items-center twc-rounded-full twc-transition-colors focus:twc-outline-none focus:twc-ring-2 focus:twc-ring-indigo-500 focus:twc-ring-offset-2`}
            >
              <span
                className={`${
                  resetProfile ? 'twc-translate-x-6' : 'twc-translate-x-1'
                } twc-inline-block twc-h-4 twc-w-4 twc-transform twc-rounded-full twc-bg-white twc-transition-transform`}
              />
            </Switch>
            <Switch.Label className="twc-prose-base">Also reset my contact info</Switch.Label>
          </div>

          <Switch.Description as="div" className="twc-not-prose">
            <p className="twc-text-xs twc-mt-2">
              Toggle &quot;on&quot; to provide a new name, email, and phone number.
            </p>
          </Switch.Description>
        </Switch.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={() => onClose()} size="large" variant="outline">
          Cancel
        </Button>

        <Button size="large" onClick={onReset} className="twc-bg-rose-700">
          Confirm &amp; go to Contact/Project Info
          <ClockCounterClockwise />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
